import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "TarjetaUID";

/* =========================================================
   FUNCIONES AUXILIARES
========================================================= */

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .trim()
    .toLowerCase();

const escaparHTML = (valor = "") => {
  const caracteres = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };

  return String(valor).replace(
    /[&<>"']/g,
    (caracter) => caracteres[caracter]
  );
};

const authConfig = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const obtenerPermisosModulo = () => {
  try {
    const permisosStorage =
      localStorage.getItem("permisos");

    const usuarioStorage =
      localStorage.getItem("usuario");

    const userStorage =
      localStorage.getItem("user");

    const usuariosStorage =
      localStorage.getItem("usuarios");

    const permisosData = permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};

    const modulos = Array.isArray(permisosData)
      ? permisosData
      : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

    return (
      modulos.find((modulo) => {
        const nombreModulo =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          "";

        return (
          normalizarTexto(nombreModulo) ===
          normalizarTexto(MODULO)
        );
      }) || {}
    );
  } catch (error) {
    console.error(
      "Error leyendo permisos:",
      error
    );

    return {};
  }
};

const obtenerNombreCompleto = (aprendiz = {}) => {
  const nombreCompleto = [
    aprendiz.nombre1Aprendiz,
    aprendiz.nombre2Aprendiz,
    aprendiz.apellido1Aprendiz,
    aprendiz.apellido2Aprendiz,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return nombreCompleto || "N/A";
};

const formatearFecha = (fecha) => {
  if (!fecha) {
    return "—";
  }

  const fechaConvertida = new Date(fecha);

  if (Number.isNaN(fechaConvertida.getTime())) {
    return "—";
  }

  return fechaConvertida.toLocaleDateString(
    "es-CO",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }
  );
};

/**
 * Estilo en línea para conservar los botones
 * cuadrados sin utilizar un archivo CSS.
 */
const obtenerEstiloBoton = (habilitado) => ({
  width: 36,
  height: 36,
  minWidth: 36,
  padding: 0,
  opacity: habilitado ? 1 : 0.5,
  cursor: habilitado
    ? "pointer"
    : "not-allowed",
});

/* =========================================================
   COMPONENTE
========================================================= */

const ListarTarjetasAsignadas = ({
  refresh,
}) => {
  const [
    asignaciones,
    setAsignaciones,
  ] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [
    procesandoId,
    setProcesandoId,
  ] = useState("");

  const navigate = useNavigate();

  /* =======================================================
     PERMISOS
  ======================================================= */

  const permisosModulo = useMemo(
    () => obtenerPermisosModulo(),
    []
  );

  const puedeCrear =
    permisosModulo?.puedeCrear === true ||
    permisosModulo?.crear === true;

  const puedeActualizar =
    permisosModulo?.puedeActualizar === true ||
    permisosModulo?.actualizar === true ||
    permisosModulo?.editar === true;

  const puedeEliminar =
    permisosModulo?.puedeEliminar === true ||
    permisosModulo?.eliminar === true;

  /*
   * Se mantiene compatibilidad con tu sistema:
   * actualizar o eliminar permiten cambiar el estado.
   */
  const puedeGestionarEstado =
    puedeActualizar || puedeEliminar;

  const hayProcesoActivo =
    loading || Boolean(procesandoId);

  /* =======================================================
     CARGAR ASIGNACIONES
  ======================================================= */

  const getData = useCallback(
    async (mostrarCarga = true) => {
      try {
        if (mostrarCarga) {
          setLoading(true);
        }

        const response = await axios.get(
          "/api/tarjetas/listall",
          authConfig()
        );

        const data = response.data || {};

        if (data.ok === false) {
          throw new Error(
            data.message ||
              "No se pudieron cargar las tarjetas."
          );
        }

        const registros = Array.isArray(
          data.asignaciones
        )
          ? data.asignaciones
          : [];

        /*
         * El controller entrega los registros
         * ordenados desde el más reciente.
         */
        setAsignaciones(registros);
      } catch (error) {
        console.error(
          "Error cargando las tarjetas:",
          error
        );

        setAsignaciones([]);

        if (error?.response?.status === 401) {
          await Swal.fire({
            icon: "warning",
            title: "Sesión expirada",
            text:
              "Por favor inicia sesión nuevamente.",
            confirmButtonText: "Aceptar",
            allowOutsideClick: false,
            allowEscapeKey: false,
          });

          localStorage.removeItem("token");

          navigate("/login");

          return;
        }

        await Swal.fire({
          icon: "error",
          title:
            "No se pudieron cargar las tarjetas",
          text:
            error.response?.data?.message ||
            error.message ||
            "Ocurrió un error al consultar las tarjetas.",
          confirmButtonText: "Aceptar",
        });
      } finally {
        if (mostrarCarga) {
          setLoading(false);
        }
      }
    },
    [navigate]
  );

  /* =======================================================
     EDITAR ASIGNACIÓN
  ======================================================= */

  const editarTarjeta = useCallback(
    async (tarjeta) => {
      if (!puedeActualizar) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text:
            "No tiene permisos para editar asignaciones de tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!tarjeta?._id) {
        await Swal.fire({
          icon: "error",
          title: "Registro no válido",
          text:
            "No se encontró el identificador de la asignación.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      navigate(
        `/tarjeta/editar/${tarjeta._id}`,
        {
          state: {
            asignacion: tarjeta,
          },
        }
      );
    },
    [
      navigate,
      puedeActualizar,
    ]
  );

  /* =======================================================
     ACTIVAR O DESACTIVAR
  ======================================================= */

  const cambiarEstadoTarjeta = useCallback(
    async (tarjeta) => {
      if (!puedeGestionarEstado) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text:
            "No tiene permisos para cambiar el estado de las tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!tarjeta?._id) {
        await Swal.fire({
          icon: "error",
          title: "Registro no válido",
          text:
            "No se encontró el identificador de la asignación.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      const estaActiva =
        tarjeta.estado === "ACTIVA";

      const accion = estaActiva
        ? "desactivar"
        : "activar";

      const aprendiz =
        tarjeta.aprendizObjectId || {};

      const confirmacion =
        await Swal.fire({
          icon: "question",
          title: `Confirmar ${accion}`,
          html: `
            <div class="text-start">
              <p>
                ¿Desea ${accion} esta tarjeta?
              </p>

              <div class="border rounded-3 overflow-hidden">
                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    Aprendiz
                  </span>

                  <strong class="text-end">
                    ${escaparHTML(
                      obtenerNombreCompleto(
                        aprendiz
                      )
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    Documento
                  </span>

                  <strong>
                    ${escaparHTML(
                      aprendiz.documentoAprendiz ||
                        "N/A"
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    UID normalizado
                  </span>

                  <strong class="font-monospace">
                    ${escaparHTML(
                      tarjeta.tarjetaUID ||
                        "N/A"
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2">
                  <span class="text-secondary">
                    Estado actual
                  </span>

                  <span class="badge ${
                    estaActiva
                      ? "text-bg-success"
                      : "text-bg-secondary"
                  }">
                    ${escaparHTML(
                      tarjeta.estado ||
                        "N/A"
                    )}
                  </span>
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText:
            estaActiva
              ? "Sí, desactivar"
              : "Sí, activar",
          cancelButtonText: "Cancelar",
          confirmButtonColor:
            estaActiva
              ? "#6c757d"
              : "#198754",
          cancelButtonColor: "#212529",
          reverseButtons: true,
          focusCancel: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

      if (!confirmacion.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(tarjeta._id);

        const response = await axios.put(
          `/api/tarjetas/desactivar/${tarjeta._id}`,
          {},
          authConfig()
        );

        await getData(false);

        await Swal.fire({
          icon: "success",
          title:
            estaActiva
              ? "Tarjeta desactivada"
              : "Tarjeta activada",
          text:
            response.data?.message ||
            `La tarjeta fue ${
              estaActiva
                ? "desactivada"
                : "activada"
            } correctamente.`,
          confirmButtonText: "Aceptar",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
      } catch (error) {
        console.error(
          "Error cambiando el estado:",
          error
        );

        await Swal.fire({
          icon: "error",
          title:
            "No se pudo cambiar el estado",
          text:
            error.response?.data?.message ||
            "Ocurrió un error al cambiar el estado de la tarjeta.",
          confirmButtonText: "Aceptar",
        });
      } finally {
        setProcesandoId("");
      }
    },
    [
      getData,
      puedeGestionarEstado,
    ]
  );

  /* =======================================================
     ELIMINAR ASIGNACIÓN
  ======================================================= */

  const eliminarTarjeta = useCallback(
    async (tarjeta) => {
      if (!puedeEliminar) {
        await Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text:
            "No tiene permisos para eliminar asignaciones de tarjetas.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      if (!tarjeta?._id) {
        await Swal.fire({
          icon: "error",
          title: "Registro no válido",
          text:
            "No se encontró el identificador de la asignación.",
          confirmButtonText: "Aceptar",
        });

        return;
      }

      const aprendiz =
        tarjeta.aprendizObjectId || {};

      const confirmacion =
        await Swal.fire({
          icon: "warning",
          title: "Eliminar asignación",
          html: `
            <div class="text-start">
              <p>
                La asignación será eliminada
                permanentemente.
              </p>

              <div class="border rounded-3 overflow-hidden">
                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    Aprendiz
                  </span>

                  <strong class="text-end">
                    ${escaparHTML(
                      obtenerNombreCompleto(
                        aprendiz
                      )
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    Documento
                  </span>

                  <strong>
                    ${escaparHTML(
                      aprendiz.documentoAprendiz ||
                        "N/A"
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    UID normalizado
                  </span>

                  <strong class="font-monospace">
                    ${escaparHTML(
                      tarjeta.tarjetaUID ||
                        "N/A"
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                  <span class="text-secondary">
                    UID celular
                  </span>

                  <strong class="font-monospace text-break">
                    ${escaparHTML(
                      tarjeta.tarjetaUIDCompleto ||
                        "—"
                    )}
                  </strong>
                </div>

                <div class="d-flex justify-content-between align-items-center gap-3 px-3 py-2">
                  <span class="text-secondary">
                    Código USB
                  </span>

                  <strong class="font-monospace">
                    ${escaparHTML(
                      tarjeta.tarjetaUIDLectorDecimal ||
                        "—"
                    )}
                  </strong>
                </div>
              </div>

              <div class="alert alert-danger mt-3 mb-0">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>
                Esta operación no se puede deshacer.
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText:
            "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
          cancelButtonColor: "#6c757d",
          reverseButtons: true,
          focusCancel: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

      if (!confirmacion.isConfirmed) {
        return;
      }

      try {
        setProcesandoId(tarjeta._id);

        const response =
          await axios.delete(
            `/api/tarjetas/delete/${tarjeta._id}`,
            authConfig()
          );

        await getData(false);

        await Swal.fire({
          icon: "success",
          title: "Asignación eliminada",
          text:
            response.data?.message ||
            "La asignación fue eliminada correctamente.",
          confirmButtonText: "Aceptar",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
      } catch (error) {
        console.error(
          "Error eliminando la asignación:",
          error
        );

        await Swal.fire({
          icon: "error",
          title: "No se pudo eliminar",
          text:
            error.response?.data?.message ||
            "Ocurrió un error al eliminar la asignación.",
          confirmButtonText: "Aceptar",
        });
      } finally {
        setProcesandoId("");
      }
    },
    [
      getData,
      puedeEliminar,
    ]
  );

  /* =======================================================
     FILAS DE LA TABLA
  ======================================================= */

  const tarjetas = useMemo(() => {
    return asignaciones.map((tarjeta) => {
      const aprendiz =
        tarjeta.aprendizObjectId || {};

      const estaActiva =
        tarjeta.estado === "ACTIVA";

      const procesandoEstaTarjeta =
        procesandoId === tarjeta._id;

      const accionesBloqueadas =
        Boolean(procesandoId);

      const editarHabilitado =
        puedeActualizar &&
        !accionesBloqueadas;

      const estadoHabilitado =
        puedeGestionarEstado &&
        !accionesBloqueadas;

      const eliminarHabilitado =
        puedeEliminar &&
        !accionesBloqueadas;

      return {
        ...tarjeta,

        documento:
          aprendiz.documentoAprendiz ||
          "N/A",

        nombre:
          obtenerNombreCompleto(aprendiz),

        uidNormalizado:
          tarjeta.tarjetaUID ||
          "N/A",

        uidCelular:
          tarjeta.tarjetaUIDCompleto ||
          "—",

        uidUSB:
          tarjeta.tarjetaUIDLectorDecimal ||
          "—",

        fechaAsignacionTexto:
          formatearFecha(
            tarjeta.fechaAsignacion
          ),

        estadoVisual: (
          <span
            className={`badge rounded-pill px-3 py-2 ${
              estaActiva
                ? "text-bg-success"
                : "text-bg-secondary"
            }`}
          >
            <i
              className={`fa-solid ${
                estaActiva
                  ? "fa-circle-check"
                  : "fa-circle-xmark"
              } me-1`}
            />

            {tarjeta.estado || "N/A"}
          </span>
        ),

        funciones: (
          <div className="d-flex align-items-center justify-content-end gap-2 flex-nowrap">
            {/* Editar */}
            <button
              type="button"
              className="btn btn-warning btn-sm text-dark d-inline-flex align-items-center justify-content-center rounded-2"
              style={obtenerEstiloBoton(
                editarHabilitado
              )}
              disabled={!editarHabilitado}
              onClick={() =>
                editarTarjeta(tarjeta)
              }
              title={
                puedeActualizar
                  ? "Editar asignación"
                  : "Sin permiso para editar"
              }
              aria-label="Editar asignación"
            >
              <i className="fa-solid fa-pen-to-square" />
            </button>

            {/* Activar o desactivar */}
            <button
              type="button"
              className={`btn btn-sm d-inline-flex align-items-center justify-content-center rounded-2 ${
                estaActiva
                  ? "btn-secondary"
                  : "btn-success"
              }`}
              style={obtenerEstiloBoton(
                estadoHabilitado
              )}
              disabled={!estadoHabilitado}
              onClick={() =>
                cambiarEstadoTarjeta(
                  tarjeta
                )
              }
              title={
                puedeGestionarEstado
                  ? estaActiva
                    ? "Desactivar tarjeta"
                    : "Activar tarjeta"
                  : "Sin permiso para cambiar el estado"
              }
              aria-label={
                estaActiva
                  ? "Desactivar tarjeta"
                  : "Activar tarjeta"
              }
            >
              <i
                className={`fa-solid ${
                  procesandoEstaTarjeta
                    ? "fa-spinner fa-spin"
                    : estaActiva
                      ? "fa-ban"
                      : "fa-circle-check"
                }`}
              />
            </button>

            {/* Eliminar */}
            <button
              type="button"
              className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center rounded-2"
              style={obtenerEstiloBoton(
                eliminarHabilitado
              )}
              disabled={!eliminarHabilitado}
              onClick={() =>
                eliminarTarjeta(tarjeta)
              }
              title={
                puedeEliminar
                  ? "Eliminar asignación"
                  : "Sin permiso para eliminar"
              }
              aria-label="Eliminar asignación"
            >
              <i
                className={`fa-solid ${
                  procesandoEstaTarjeta
                    ? "fa-spinner fa-spin"
                    : "fa-trash-can"
                }`}
              />
            </button>
          </div>
        ),
      };
    });
  }, [
    asignaciones,
    cambiarEstadoTarjeta,
    editarTarjeta,
    eliminarTarjeta,
    puedeActualizar,
    puedeEliminar,
    puedeGestionarEstado,
    procesandoId,
  ]);

  /* =======================================================
     COLUMNAS
  ======================================================= */

  const columns = useMemo(
    () => [
      {
        Header: "Documento",
        accessor: "documento",
      },
      {
        Header: "Nombre Aprendiz",
        accessor: "nombre",
        width: 280,
        hideHeaderOnMobile: true,
      },
      {
        Header: "UID normalizado",
        accessor: "uidNormalizado",
      },
      {
        Header: "UID celular",
        accessor: "uidCelular",
        hideHeaderOnMobile: true,
      },
      {
        Header: "Código USB",
        accessor: "uidUSB",
        hideHeaderOnMobile: true,
      },
      {
        Header: "Fecha asignación",
        accessor:
          "fechaAsignacionTexto",
      },
      {
        Header: "Estado",
        accessor: "estadoVisual",
      },
      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  /* =======================================================
     EFECTOS
  ======================================================= */

  useEffect(() => {
    getData();
  }, [
    getData,
    refresh,
  ]);

  /* =======================================================
     NUEVA ASIGNACIÓN
  ======================================================= */

  const irANuevaTarjeta = async () => {
    if (!puedeCrear) {
      await Swal.fire({
        icon: "warning",
        title: "Sin permisos",
        text:
          "No tiene permisos para asignar tarjetas.",
        confirmButtonText: "Aceptar",
      });

      return;
    }

    navigate("/tarjeta/nuevo");
  };

  /* =======================================================
     INTERFAZ
  ======================================================= */

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Encabezado */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  <i className="fa-solid fa-address-card text-primary me-2" />
                  Tarjetas UID
                </h5>

                <small className="text-secondary">
                  Control y administración de
                  tarjetas asignadas a aprendices.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() =>
                    navigate("/inicio")
                  }
                  title="Volver al inicio"
                  disabled={hayProcesoActivo}
                >
                  <i className="fa-solid fa-house me-2" />
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => getData()}
                  title="Actualizar listado"
                  disabled={hayProcesoActivo}
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-arrows-rotate"
                    } me-2`}
                  />

                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={
                    !puedeCrear ||
                    hayProcesoActivo
                  }
                  style={{
                    opacity:
                      puedeCrear ? 1 : 0.5,

                    cursor:
                      puedeCrear &&
                      !hayProcesoActivo
                        ? "pointer"
                        : "not-allowed",
                  }}
                  onClick={irANuevaTarjeta}
                  title={
                    puedeCrear
                      ? "Asignar nueva tarjeta"
                      : "Sin permiso para crear"
                  }
                >
                  <i className="fa-solid fa-plus me-2" />
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-label="Cargando"
                />

                <div className="mt-3">
                  Cargando tarjetas asignadas…
                </div>
              </div>
            ) : tarjetas.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={tarjetas}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5 px-3">
                <div className="display-6 mb-2">
                  <i className="fa-regular fa-credit-card" />
                </div>

                <div className="fw-semibold">
                  No hay tarjetas asignadas
                </div>

                <div className="small mb-3">
                  No se encontraron registros de
                  tarjetas en el sistema.
                </div>

                <button
                  className="btn btn-dark"
                  type="button"
                  disabled={
                    !puedeCrear ||
                    hayProcesoActivo
                  }
                  style={{
                    opacity:
                      puedeCrear ? 1 : 0.5,

                    cursor:
                      puedeCrear &&
                      !hayProcesoActivo
                        ? "pointer"
                        : "not-allowed",
                  }}
                  onClick={irANuevaTarjeta}
                  title={
                    puedeCrear
                      ? "Asignar tarjeta"
                      : "Sin permiso para crear"
                  }
                >
                  <i className="fa-solid fa-plus me-2" />
                  Asignar tarjeta
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarTarjetasAsignadas;